import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/TopNav";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { BellRing, History } from "lucide-react";
import { AlertCreateCard } from "@/components/alerts/AlertCreateCard";
import { QuietHoursCard } from "@/components/alerts/QuietHoursCard";
import { AlertsActiveList } from "@/components/alerts/AlertsActiveList";
import { AlertsHistoryList } from "@/components/alerts/AlertsHistoryList";

export default function Alerts() {
  const { t } = useApp();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/alerts");
      setAlerts(data.alerts || []);
    } catch (e) { toast.error("Failed to load alerts"); }
    finally { setLoading(false); }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const { data } = await api.get("/alerts/history");
      setHistory(data.events || []);
    } catch (e) { toast.error("Failed to load alert history"); }
    finally { setHistLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onTabChange = (v) => { setTab(v); if (v === "triggered") loadHistory(); };

  const remove = async (id) => {
    try { await api.delete(`/alerts/${id}`); setAlerts((a) => a.filter((x) => x.id !== id)); toast.success(t("delete")); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <AppShell>
      <PageHeader title={t("alr_title")} subtitle={t("alr_subtitle")} testid="alerts-header" />
      <div className="grid gap-6 lg:grid-cols-3">
        <AlertCreateCard t={t} onCreated={load} />
        <QuietHoursCard />
        <div className="lg:col-span-2">
          <Tabs value={tab} onValueChange={onTabChange}>
            <TabsList className="mb-3" data-testid="alerts-tabs">
              <TabsTrigger value="active" data-testid="alerts-tab-active">
                <BellRing className="h-4 w-4 me-1.5" />{t("active")}{alerts.length ? ` (${alerts.length})` : ""}
              </TabsTrigger>
              <TabsTrigger value="triggered" data-testid="alerts-tab-triggered">
                <History className="h-4 w-4 me-1.5" />{t("triggered")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="active" className="m-0">
              <Card className="p-4 shadow-premium">
                <AlertsActiveList alerts={alerts} loading={loading} t={t} onDelete={remove} />
              </Card>
            </TabsContent>
            <TabsContent value="triggered" className="m-0">
              <Card className="p-4 shadow-premium">
                <AlertsHistoryList history={history} loading={histLoading} t={t} />
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </AppShell>
  );
}
